/*
 * The Ultimate Preset Pack page's built graphics (snippets/ultimate-pack-graphic.liquid,
 * styled in assets/ultimate-pack-graphics.css).
 *
 * [data-live] plays a scene's moment: while the pointer is over its chapter
 * on a computer, or once the chapter is in view on a touch screen (a tap
 * plays or rests it there too). The pointer also tilts the scene a little
 * (--gx, --gy from -1 to 1), and the loops (the rolling grid, the streaks,
 * the ring) speed up while live by easing their playback rate, so they
 * never jump. Off screen the loops rest ([data-asleep]).
 *
 * A number marked [data-upp-count] counts up to itself each time its scene
 * plays (from data-from, or from 1), the way the Number Counter template
 * would animate it; at rest it shows its final value.
 */
(() => {
  if (window.ultimatePackGraphics) {
    window.ultimatePackGraphics.arm(document);
    return;
  }

  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  const init = (gfx) => {
    if (gfx.hasAttribute('data-upp-gfx-ready')) return;
    gfx.setAttribute('data-upp-gfx-ready', '');

    const host = gfx.closest('[data-scene]') || gfx.parentElement;
    // The chapter also contains copy. Only the framed panel owns hover.
    const monitor = gfx.closest('.mogrt-shot, [class*="upp-story__monitor-"]') || gfx.parentElement.parentElement || gfx.parentElement;
    const fast = { speed: 4 }[gfx.dataset.uppGfx] || 3;
    // Keep the native float, orbit and scene loops moving gently at rest.
    // Hover eases their speed up; offscreen CSS still pauses them.
    const restRate = 1;
    const finishCountsAtRest = host.matches('.mogrt-chapter, .mogrt-story');

    // ease the loops' speed towards the live (or resting) rate
    let rate = restRate;
    let target = restRate;
    let easing = 0;
    let lastEase = null;
    let settling = [];
    let rateAnimations = [];
    let wiggleSpeed = 1;
    let wiggleTarget = 1;
    const loops = () =>
      gfx.getAnimations ? gfx.getAnimations({ subtree: true }).filter((a) => a.effect?.getTiming().iterations === Infinity) : [];
    const ease = (now) => {
      const elapsed = lastEase === null ? 0 : Math.max(0, Math.min(64, now - lastEase));
      lastEase = now;
      rate += (target - rate) * (1 - Math.exp(-elapsed / 230));
      if (Math.abs(target - rate) < 0.01) rate = target;
      wiggleSpeed += (wiggleTarget - wiggleSpeed) * (1 - Math.exp(-elapsed / 230));
      if (Math.abs(wiggleTarget - wiggleSpeed) < 0.01) wiggleSpeed = wiggleTarget;
      rateAnimations.forEach((animation) => {
        const nextRate = rate * (animation.animationName === 'upp-gfx-wiggle' ? wiggleSpeed : 1);
        if (animation.updatePlaybackRate) animation.updatePlaybackRate(nextRate);
        else animation.playbackRate = nextRate;
      });
      easing = rate === target && wiggleSpeed === wiggleTarget ? 0 : requestAnimationFrame(ease);
    };

    if (!still.matches) loops().forEach((animation) => {
      if (animation.updatePlaybackRate) animation.updatePlaybackRate(restRate);
      else animation.playbackRate = restRate;
    });

    // count-ups: every layer of an extruded number shows the same value
    const counts = [...gfx.querySelectorAll('[data-upp-count]')].map((number) => {
      const layers = [...number.children];
      const [, before = '', digits = '0', after = ''] = (layers[0]?.textContent.trim() || '').match(/^(\D*)([\d.,]+)(.*)$/) || [];
      const to = parseFloat(digits.replace(/,/g, '')) || 0;
      const from = Number(number.dataset.from ?? 1);
      let frame = 0;
      let current = to;
      let shown = layers[0]?.textContent || '';
      const write = (value) => {
        current = value;
        const text = `${before}${Math.round(value)}${after}`;
        if (text === shown) return;
        shown = text;
        layers.forEach((layer) => {
          layer.textContent = text;
        });
      };
      const run = (begin, duration) => {
        cancelAnimationFrame(frame);
        if (still.matches || !to || begin === to) return write(to);
        let start = null;
        const step = (now) => {
          if (start === null) start = now;
          const t = Math.max(0, Math.min(1, (now - start) / Math.max(1, duration)));
          write(begin + (to - begin) * (1 - Math.pow(1 - t, 3)));
          if (t < 1) frame = requestAnimationFrame(step);
        };
        write(begin);
        frame = requestAnimationFrame(step);
      };
      return {
        play() {
          run(from, Math.min(1600, 600 + Math.abs(to - from) * 40));
        },
        finish() {
          // Complete an interrupted count from its current value instead
          // of snapping the number when the pointer leaves.
          run(current, 650);
        },
      };
    });

    const live = (on) => {
      if (gfx.hasAttribute('data-live') === on) return;
      // Capture CSS-only moments before removing their live state. The
      // permanent loops keep their phase and ease their speed separately.
      const leaving = on || still.matches || !gfx.getAnimations ? [] :
        gfx.getAnimations({ subtree: true })
          .filter((animation) => animation.animationName && animation.playState === 'running')
          .map((animation) => {
            const node = animation.effect.target;
            const pseudo = animation.effect.pseudoElement;
            const style = getComputedStyle(node, pseudo);
            const shown = { offset: 0 };
            animation.effect.getKeyframes().forEach((keyframe) => {
              Object.keys(keyframe).forEach((name) => {
                if (!['offset', 'computedOffset', 'easing', 'composite'].includes(name)) shown[name] = style[name];
              });
            });
            return { animation, node, pseudo, shown };
          });
      settling.forEach((animation) => animation.cancel());
      settling = [];
      gfx.toggleAttribute('data-live', on);
      if (on) counts.forEach((count) => count.play());
      else if (finishCountsAtRest) counts.forEach((count) => count.finish());
      leaving.forEach(({ animation, node, pseudo, shown }) => {
        const style = getComputedStyle(node, pseudo);
        const rest = { offset: 1 };
        Object.keys(shown).forEach((name) => {
          if (name !== 'offset') rest[name] = style[name];
        });
        // Ignore animations still attached: the float, orbit and grid
        // should continue without being replaced by a return animation.
        if (animation.playState !== 'idle') return;
        // A polygon cannot interpolate to "none". Open the paper to its
        // matching flat polygon; other cutouts keep their shape while the
        // wave or parent paper layer fades out.
        if (shown.clipPath && rest.clipPath === 'none') {
          const flat = animation.animationName === 'mpc-cut' ?
            animation.effect.getKeyframes().find((frame) => frame.computedOffset >= 0.26 && frame.computedOffset <= 0.64) : null;
          rest.clipPath = flat?.clipPath || shown.clipPath;
        }
        try {
          settling.push(node.animate([shown, rest], {
            duration: 950,
            easing: 'cubic-bezier(0.22, 0.8, 0.25, 1)',
            ...(pseudo ? { pseudoElement: pseudo } : {})
          }));
        } catch (error) {
          // The resting CSS picture remains usable without Web Animations.
        }
      });
      target = on && !still.matches ? fast : restRate;
      rateAnimations = loops();
      if (!easing) {
        lastEase = null;
        easing = requestAnimationFrame(ease);
      }
    };

    // tilt with the pointer, at most once a frame
    let tilt = 0;
    let gx = 0;
    let gy = 0;
    const lean = () => {
      tilt = 0;
      gfx.style.setProperty('--gx', gx.toFixed(3));
      gfx.style.setProperty('--gy', gy.toFixed(3));
    };
    const clamp = (n) => Math.max(-1, Math.min(1, n));

    if (pointer.matches) {
      monitor.addEventListener('pointerenter', () => live(true));
      monitor.addEventListener('pointerleave', () => {
        live(false);
        gx = 0;
        gy = 0;
        if (!tilt) tilt = requestAnimationFrame(lean);
      });
      monitor.addEventListener('pointermove', (event) => {
        if (still.matches) return;
        const box = monitor.getBoundingClientRect();
        gx = clamp(((event.clientX - box.left) / box.width - 0.5) * 2);
        gy = clamp(((event.clientY - box.top) / box.height - 0.5) * 2);
        if (!tilt) tilt = requestAnimationFrame(lean);
      });
    } else {
      // touch: play once the chapter has settled into view, rest when it leaves
      let wait = 0;
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          ([entry]) => {
            clearTimeout(wait);
            if (entry.isIntersecting) wait = setTimeout(() => live(true), still.matches ? 0 : 900);
            else live(false);
          },
          { threshold: 0.6 }
        ).observe(gfx);
      }
      gfx.addEventListener('click', () => {
        clearTimeout(wait);
        live(!gfx.hasAttribute('data-live'));
      });
    }

    let cycle = () => {};
    if (gfx.dataset.uppGfx === 'wiggle') {
      const states = [
        { speed: 1, amount: 1, own: 1, shared: 0.2, sliders: [30, 40, 20] },
        { speed: 1.35, amount: 1.35, own: 0.4, shared: 0.9, sliders: [75, 60, 80] },
        { speed: 0.75, amount: 0.65, own: 0.75, shared: 0.45, sliders: [20, 25, 40] }
      ];
      let state = 0;
      let timer = 0;
      const sliders = [...gfx.querySelectorAll('.eg-slider')];
      const apply = () => {
        const settings = states[state];
        gfx.style.setProperty('--upp-wg-amount', settings.amount);
        gfx.style.setProperty('--upp-wg-own', settings.own);
        gfx.style.setProperty('--upp-wg-shared', settings.shared);
        sliders.forEach((slider, index) => {
          if (settings.sliders[index] === undefined) return;
          slider.style.setProperty('--a', `${settings.sliders[index]}%`);
          slider.style.setProperty('--b', `${settings.sliders[index]}%`);
        });
        wiggleTarget = settings.speed;
        rateAnimations = loops();
        if (!still.matches && !easing) {
          lastEase = null;
          easing = requestAnimationFrame(ease);
        }
      };
      cycle = () => {
        clearTimeout(timer);
        timer = 0;
        if (!gfx.isConnected || gfx.hasAttribute('data-asleep') || document.hidden || still.matches) return;
        timer = setTimeout(() => {
          if (!gfx.isConnected || gfx.hasAttribute('data-asleep') || document.hidden || still.matches) return;
          state = (state + 1) % states.length;
          apply();
          cycle();
        }, 4000);
      };
      if (!still.matches) apply();
      document.addEventListener('visibilitychange', cycle);
      still.addEventListener('change', cycle);
    }

    // rest the loops while off screen
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        gfx.toggleAttribute('data-asleep', !entry.isIntersecting);
        cycle();
      }, {
        rootMargin: '120px 0px',
      }).observe(gfx);
    } else cycle();
  };

  const arm = (root) => root.querySelectorAll('[data-upp-gfx]').forEach(init);
  window.ultimatePackGraphics = { arm };
  arm(document);
  document.addEventListener('shopify:section:load', (event) => arm(event.target));
})();

